/**
 * Standalone prototype object containing all client-system method implementations.
 *
 * `ClientProcess.decorate()` copies these methods/accessors onto any `System`
 * instance to give it full client capabilities.  This replaces the old
 * `ClientSystem` class prototype — we no longer need a class for the method bodies.
 *
 * @internal
 */

import {
  PEER_CONNECTION_STATUS,
  PROTOCOLS,
  type SystemMessage,
  type AreaSystemRequest,
} from "@gotti/core";
import type { BaseClientConnector } from "./ClientConnectors/BaseClientConnector.js";
import type { Client } from "./Client.js";
import type { IndexedDBLoader } from './IndexedDbLoader.js';

// ── Build the prototype object ───────────────────────────────────────────────
const proto: Record<string, any> = Object.create(null);

/**
 * The area this system's server sends belong to — the area file that started
 * it (see ./areaRouting). Throws when the system belongs to more than one
 * joined area, or to none at all, so the caller names one explicitly.
 *
 * `undefined` means routing does not apply: the process is not networked, or
 * this system sits on a bare process with no area bookkeeping (the in-process
 * unit-test harnesses). Those send un-addressed, as every send did before owner
 * routing existed.
 */
function sendAreaIdFor(system: any): string | undefined {
  const process = system._$client?.process;
  if (!process || typeof process.resolveAreaForSystem !== 'function') return undefined;
  return process.resolveAreaForSystem(system.name);
}

/**
 * Same, for `requestArea` — which, unlike a fire-and-forget dispatch, can not
 * travel at all without an areaId. Where routing does not apply there is still
 * a request to address, so fall back to the pre-routing rule: the single joined
 * area, else throw.
 */
function requestAreaIdFor(system: any): string {
  const resolved = sendAreaIdFor(system);
  if (resolved !== undefined) return resolved;
  const joined = Object.keys(system.$areaData || {});
  if (joined.length === 1) return joined[0];
  throw new Error(
    joined.length
      ? 'No areaId provided and the client is joined to multiple areas. Please specify an areaId.'
      : 'No areaId provided and the client is not joined to any areas.',
  );
}

// ── Methods ──────────────────────────────────────────────────────────────────

proto.initialize = function (this: any, client: Client, connector: BaseClientConnector, indexedDb?: IndexedDBLoader) {
  this._$client = client;
  this.connector = connector;
  this.indexedDb = indexedDb;
};

proto.dispatchToServer = function (this: any, message: SystemMessage, reliable?: boolean) {
  const areaId = sendAreaIdFor(this);
  if (areaId !== undefined) {
    this.connector.send([PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE, message.type, message.data, areaId, message.to], reliable);
    return;
  }
  this.connector.send([PROTOCOLS.CLIENT_TO_SERVER_MESSAGE, message.type, message.data, message.to], reliable);
};

proto.instantDispatchToServer = function (this: any, message: SystemMessage, reliable?: boolean) {
  const areaId = sendAreaIdFor(this);
  if (areaId !== undefined) {
    this.connector.send([PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE_INSTANT, message.type, message.data, areaId, message.to], reliable);
    return;
  }
  this.connector.send([PROTOCOLS.CLIENT_TO_SERVER_MESSAGE_INSTANT, message.type, message.data, message.to], reliable);
};

proto.dispatchRaw = function (this: any, data: ArrayBuffer) {
  this.connector.sendRaw(data, undefined, undefined, sendAreaIdFor(this));
};

proto.dispatchToArea = function (this: any, areaId: string, message: SystemMessage, reliable?: boolean) {
  this.connector.send([PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE, message.type, message.data, areaId, message.to], reliable);
};

proto.requestArea = async function (this: any, message: AreaSystemRequest): Promise<any> {
  // No areaId given → the area file that started this system (a request can
  // not travel without a target, hence `required`).
  if (message.areaId === undefined || message.areaId === null || (message as any).areaId === '') {
    message.areaId = requestAreaIdFor(this);
  }
  return this.connector.requestServer(message);
};

proto.dispatchToAllPeers = function (this: any, message: SystemMessage) {
  this.connector.sendAllPeersMessage(message);
};

proto.dispatchToAllPeersAndServer = function (this: any, message: SystemMessage) {
  this.connector.sendAllPeersMessage(message);
  this.dispatchToServer(message);
};

proto.instantDispatchToAllPeersAndServer = function (this: any, message: SystemMessage) {
  this.connector.sendAllPeersMessage(message);
  this.instantDispatchToServer(message);
};

proto.dispatchToPeers = function (this: any, playerIds: string[], message: SystemMessage) {
  this.connector.sendToPeers(playerIds, message);
};

proto.dispatchToPeer = function (this: any, playerId: string, message: SystemMessage) {
  this.connector.sendToPeer(playerId, message);
};

proto.dispatchToPeerRaw = function (this: any, playerId: string | number, data: ArrayBuffer, to?: string | number) {
  this.connector.sendPeerRaw(playerId, data, to);
};

proto.dispatchToPeersRaw = function (this: any, playerIds: string[], data: ArrayBuffer, to?: string | number) {
  this.connector.sendPeersRaw(playerIds, data, to);
};

proto.addPeer = async function (this: any, playerPublicId: string, options?: Record<string, any>, ackTimeout?: number, requestTimeout?: number) {
  options = options ? options : {};
  if (!this.onPeerSystemConnectionRequest) {
    throw new Error(
      `Cannot add a peer from the system ${this.name}. This system does not implement onPeerSystemConnectionRequest which needs to be implemented and return a truthy value to accept the request. Or falsey/throw an error to reject the request.`
    );
  }
  return new Promise((resolve, reject) => {
    this.connector.requestPeerConnection(playerPublicId, this.name, options, (err: any, _options: any) => {
      if (err) {
        return reject(err);
      } else {
        _options = _options ? _options : true;
        return resolve(_options);
      }
    }, ackTimeout, requestTimeout);
  });
};

proto.handleUpdateState = function (this: any, areaId: string, state: any) {
  const previous = this.states[areaId];
  this.states[areaId] = state;
  this.onAreaSystemStateUpdate?.(areaId, state, previous);
};

proto.removePeer = function (this: any, playerId: string | number) {
  this.connector.stopPeerConnection(playerId);
};

proto.getPeerPing = function (this: any, playerId: string | number) {
  return this.connector.peerConnections[playerId]?.ping ?? -1;
};

proto.getPeerStatus = function (this: any, playerId: string | number) {
  if (playerId in this.connector.peerConnections) {
    const { status, connectedAt } = this.connector.peerConnections[playerId];
    return { status, connectedAt, failures: this.connector.peerFailures[playerId] || [] };
  } else {
    return { status: PEER_CONNECTION_STATUS.NONE, failures: this.connector.peerFailures[playerId] || [] };
  }
};

proto.isConnectedToPeer = function (this: any, playerId: string | number) {
  return this.connector.peerConnections[playerId]?.status === PEER_CONNECTION_STATUS.CONNECTED;
};

// ── Accessors (getters / setters) ────────────────────────────────────────────
// We need to use Object.defineProperty so that Object.getOwnPropertyDescriptor
// returns proper accessor descriptors for decorate() to copy.

Object.defineProperty(proto, 'client', {
  get(this: any) { return this._$client; },
  set(this: any) { throw new Error('Do not set the client value'); },
  configurable: true,
  enumerable: false,
});

Object.defineProperty(proto, '$client', {
  get(this: any) { return this._$client; },
  set(this: any) { throw new Error('Do not set the client value'); },
  configurable: true,
  enumerable: false,
});

Object.defineProperty(proto, 'connectedPeers', {
  get(this: any) { return this.connector.connectedPeerIds; },
  configurable: true,
  enumerable: false,
});

/**
 * The prototype object whose methods/accessors are copied onto System
 * instances by `ClientProcess.decorate()`.
 */
export const CLIENT_SYSTEM_PROTO = proto;
